<template>
	<Default :loading="loading">
		<GeolocationWarnings v-if="geolocationError" :error="geolocationError" :granted="granted" :support="supported" />
		<div v-else class="w-full h-full relative md:flex">
			<div class="grow flex flex-col md:translate-x-0 transform transition-transform duration-300 ease-in-out translate-x-0 h-full">
				<div class="sticky top-14 bg-white z-10" v-show="current">
					<div class="flex items-center justify-between border-b border-gray-200 px-4 sm:px-6 md:px-5 h-16">
						<Button color="tertiary" @click="current = null">
							<icon name="ri-arrow-left-line" class="opacity-70 w-5 h-5 mr-1" />
							Atrás
						</Button>
					</div>
				</div>
				<!-- Container -->
				<div class="grow p-3 bg-gray-100">
					<!-- Page header -->
					<div v-show="!current" class="md:flex md:items-center mb-3">
						<!-- Left: Title -->
						<h1 class="text-2xl md:text-3xl text-gray-800 font-bold mb-2 md:mb-0 flex-1">Leads cualificados</h1>
					</div>

					<!-- More actions -->
					<Accordion v-if="!current" title="Filtros" class="bg-white">
						<div class="grid md:grid-cols-2 items-start gap-2 mt-3 overflow-auto">
							<select v-model="options.origin" class="w-full form-select">
								<option v-for="origin in origins" :key="origin._id" :value="origin._id">
									{{ origin.label }}
								</option>
							</select>

							<select name="project" v-model="options.project" class="w-full form-select">
								<option :value="undefined" disabled="true">Selecciona proyecto</option>
								<option v-for="project in projects" :key="project._id" :value="project.name">
									{{ project.label }}
								</option>
							</select>

							<input v-model="options['price>']" class="form-input focus:border-gray-300 w-full" type="number" placeholder="Precio desde" :min="0" :max="100000" @input="e => onChangeSearchInput(e, 'priceFrom')" />
							<input v-model="options['price<']" class="form-input focus:border-gray-300 w-full" type="number" placeholder="Precio hasta" :min="0" :max="100000" @input="e => onChangeSearchInput(e, 'priceTo')" />

							<input v-model="options['contract.address.zipcode']" class="form-input focus:border-gray-300 w-full" type="text" placeholder="Código postal" @input="e => onChangeSearchInput(e, 'zipcode')" />
						</div>
					</Accordion>

					<!-- Table -->
					<div v-if="!current" class="bg-white rounded-sm border border-gray-200 relative mt-3">
						<div class="md:flex md:items-center py-2 px-3">
							<header class="mb-2 md:mb-0 flex-1 font-semibold text-slate-800">
								Resultados:
								<span class="text-slate-400">{{ total }}</span>
							</header>
							<div class="gap-2 flex items-center">
								Mostrar:
								<select v-model="options.limit" class="ml-2 form-select flex-1 md:flex-auto" @change="onChangeLimit">
									<option :value="5">5</option>
									<option :value="10">10</option>
									<option :value="20">20</option>
									<option :value="50">50</option>
									<option :value="100">100</option>
									<option :value="150">150</option>
								</select>
							</div>
						</div>

						<!-- Table -->
						<div class="overflow-x-auto" v-if="!loading && items.length">
							<table class="table-auto w-full">
								<!-- Table header -->
								<thead class="text-xs font-semibold uppercase text-gray-500 bg-gray-50 border-b border-t border-gray-200">
									<tr>
										<th class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('contract.customer.name')">
													<p class="uppercase mr-1">Nombre</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'contract.customer.name'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>

										<th class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('contract.address.zipcode')">
													<p class="uppercase mr-1">Código postal</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'contract.address.zipcode'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>

										<th class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('price')">
													<p class="uppercase mr-1">Precio</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'price'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>
									</tr>
								</thead>
								<!-- Table body -->
								<tbody class="text-sm divide-y divide-gray-200">
									<tr
										v-for="item in items"
										:key="item._id"
										class="even:bg-sky-100 hover:bg-sky-200 cursor-pointer"
										@click.stop="
											() => {
												current = item
											}
										"
									>
										<td class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-medium">{{ item.contract.customer.name }}{{ item.contract.customer.lastname ? ` ${item.contract.customer.lastname}` : '' }}</div>
										</td>

										<td class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-medium">
												{{ item.contract.address.zipcode }}
											</div>
										</td>

										<td class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-medium">{{ item.price }}€</div>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<Lead v-if="current" :_id="current._id" :project="options.project" :coordinates="coordinates" :lockOnFetch="true" @lock="onLock" class="!p-0" />

					<!-- Pagination -->
					<div class="mt-8 mb-24" v-if="!loading && items.length && !current">
						<Pagination v-show="total > options.limit" :results="items.length" :skip="options.skip" :limit="options.limit" :total="total" @page="updatePage" />
					</div>

					<!-- No results -->
					<div class="bg-white p-4 rounded-sm border border-gray-200 mt-3" v-if="(!loading && !items.length) || error.message">
						<Banner v-if="!!error.message" type="error" :open="!!error?.message" fixed>
							<p>{{ error.message }}</p>
							<ul v-if="error?.errors">
								<li v-for="(err, i) in error.errors" :key="i">
									<p class="p-2">- {{ err.message }}</p>
								</li>
							</ul>
						</Banner>
						<div class="w-full flex justify-center p-4" v-if="!loading && !items.length">
							<div class="text-center py-6">
								<div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-red-300 to-red-100 mb-4">
									<icon name="ri-information-line" scale="2" />
								</div>
								<h2 class="text-2xl text-gray-800 font-bold mb-2">No se han encontrado oportunidades.</h2>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Default>
</template>

<script setup lang="ts">
import { ValidationError } from '@/errors/ValidationError'
import { user } from '@/user'
import { Ref, computed, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Accordion from '../../components/Accordion.vue'
import Banner from '../../components/Banner.vue'
import Button from '../../components/Button.vue'
import GeolocationWarnings from '../../components/geolocation/GeolocationWarnings.vue'
import Pagination from '../../components/Pagination.vue'
import { Origin } from '../../interfaces/Origin'
import { Project } from '../../interfaces/Project'
import { get } from '../../services/api'
import { useGeolocation } from '../../services/geolocation'
import { strToRegExp } from '../../utils/strToRegExp'
import Lead from '../deal/Lead.vue'
import Default from '../Default.vue'
import { queryRegExpToStr } from '../../utils/queryRegExpToStr'
import { Lead as LeadInterface } from '../../interfaces/Lead'

const route = useRoute()
const router = useRouter()

const current = ref<LeadInterface>()

const hasInitialized = ref(false)

const { coordinates, supported, granted, error: geolocationError } = useGeolocation()

const origins: Ref<Origin[]> = ref([])

const options: Record<string, any> = reactive({
	...route.query,
	limit: route.query.limit ? parseInt(<string>route.query.limit) : 20,
	skip: route.query.skip ? parseInt(<string>route.query.skip) : 0,
	sort: route.query.sort || '-createdAt',
	'contract.address.zipcode': route.query['contract.address.zipcode'] ? queryRegExpToStr(route.query['contract.address.zipcode'].toString()) : undefined,
	...((route.query.project && { project: route.query.project }) || (user.value?.projects?.length && { project: user.value?.projects[0] })),
	...((route.query.origin && { origin: route.query.origin }) || (origins.value?.length && { origin: origins.value[0]._id })),
	...(route.query['price<'] && { ['price<']: route.query['price<'] }),
	...(route.query['price>'] && { ['price>']: route.query['price>'] })
})

const query = computed(() => {
	return {
		...route.query,
		...options,
		project: options.project,
		origin: options.origin,
		'contract.address.zipcode': options['contract.address.zipcode'] ? strToRegExp(options['contract.address.zipcode']) : undefined,
		'price<': options['price<'] ? options['price<'] : undefined,
		'price>': options['price>'] ? options['price>'] : undefined
	}
})

const loading = ref(false)
const items = ref<LeadInterface[]>([])
const error = ref<ValidationError>(<any>{})
const total = ref(0)
const projects: Ref<Project[]> = ref([])
const sortBy = reactive({ field: (<string>options.sort || '')?.split('-')[0], type: options.sort?.includes('-') ? '-' : '' })

const handleSort = (column: string) => {
	if (column != sortBy.field) {
		sortBy.field = column
		sortBy.type = ''
	} else {
		sortBy.type = sortBy.type === '-' ? '' : '-'
	}
	options.sort = `${sortBy.type}${sortBy.field}`
}

const onChangeSearchInput = (e: any, option?: string) => {
	if (!e.target.value) {
		switch (option) {
			case 'priceFrom':
				delete options['price>']
				break
			case 'priceTo':
				delete options['price<']
				break
			case 'zipcode':
				delete options['contract.address.zipcode']
				break
		}
	}
}
const fetchProjects = async () => {
	loading.value = true
	error.value = <any>{}
	try {
		const { data } = await get({ name: 'project', query: { sort: 'name', projection: '_id,name,label' } })
		projects.value = data.filter((project: Project) => user.value?.projects?.includes(project.name)) || []
		options.project = (!!(<string>route.query.project) && projects.value.find(project => route.query.project === project.name))?.name || projects.value[0].name
	} catch (err) {
		error.value = err
	}
	loading.value = false
}

const fetchOrigins = async () => {
	try {
		const { data } = await get({ name: 'origin', query: { map: 'false', hasLeads: 'true' } })
		origins.value = data
		options.origin = (!!(<string>route.query.origin) && origins.value.find(origin => route.query.origin === origin._id))?._id || origins.value[0]._id
	} catch (err) {
		error.value = err
	}
}

const search = async () => {
	loading.value = true
	error.value = <any>{}
	try {
		items.value = []
		const { project, ...sendQuery } = query.value

		const { data, pagination } = await get({ name: 'lead.no-loc', params: { project }, query: sendQuery }, coordinates.value)

		items.value = data
		total.value = pagination.total
	} catch (err) {
		error.value = err
	}
	loading.value = false
}

const onLock = (data: any) => {
	router.push({
		name: 'go.dealDashboard',
		query: {
			_id: data._id,
			project: options.project
		}
	})
}

const onChangeLimit = () => {
	options.skip = 0
}

const fetch = async () => {
	await router.replace({ query: query.value })
	await fetchOrigins()
	await fetchProjects()
	if (Object.keys(route.query).length) {
		search()
	}
}

watch([options], async () => {
	await router.replace({ query: query.value })
	if (Object.keys(route.query).length) {
		search()
	}
})

watch(
	coordinates,
	async newCoords => {
		if (Object.keys(newCoords).length > 0 && !hasInitialized.value) {
			hasInitialized.value = true
			await fetch()
		}
	},
	{ immediate: true }
)

const updatePage = (page: Ref) => {
	options.skip = page.value == -1 ? 0 : (page.value - 1) * options.limit
}
</script>
